import axios from "axios";
import React, { Suspense, useEffect, useState } from "react";
import { FaCircle } from "react-icons/fa";
import { IoCall } from "react-icons/io5";
import { FaGlobeAsia } from "react-icons/fa";
import Reviews from "@/components/common/Reviews";
import ReviewForm from "@/components/category/ReviewForm";
import TopImage from "@/components/category/TopImage";
import FAQ from "@/components/category/Faqs";
import _ from "lodash";
import Link from "next/link";
import { useRouter } from "next/router";
import Image from "next/image";
import { getLocals } from "../../../getLocal";
const LazyMap = React.lazy(() => import("@/components/category/Map"));
import { hostNameFunction } from "../../../utils/host-function";
import { getBusinessApiDetails } from "../../../utils/apiRoute";
import Head from "next/head";
import BusinessReview from "@/components/BusinessReview";
import Skeleton from "@/components/Skeleton";
import Layout from "@/components/common/Layout";
import NotFoundPage from "@/components/NotFoundPage";
import COUNTRY_NAME from "../../locals/countryName.json";
import translations from "../../locals/countryTranslation.json";

export async function getServerSideProps(context) {
  const { req, resolvedUrl } = context;
  const pro = req.headers["x-forward-proto"] || "https";
  const h = req.headers["host"];
  const full = `${pro}://${h}${resolvedUrl}`;
  const { businessSlug } = context.params;
  const getHttp = process.env.BASE_URL;
  const currentUrl = `${getHttp}://${context.req.headers.host}${context.req.url}`;
  const hostname = hostNameFunction(context.req);

  // Get base URL by removing subdomains
  let baseUrl = currentUrl.split(".com")[0] + ".com"; // Extract base URL before `.com`

  // Extract what comes after `https://`
  let domainPart = baseUrl.replace(/^https?:\/\//, "");

  // Check for `hostname.` or `hostname-` at the start of the domainPart
  if (domainPart.startsWith(`${hostname}.`)) {
    // Replace language subdomain with "www."
    baseUrl = baseUrl.replace(
      new RegExp(`^https?://${hostname}[.]`),
      "https://www."
    );
  } else if (domainPart.startsWith(`${hostname}-`)) {
    // Remove language prefix and hyphen
    baseUrl = baseUrl.replace(new RegExp(`^https?://${hostname}-`), "https://");
  }
  const country = COUNTRY_NAME[baseUrl] || "Unknown Country";

  // Get the language or default to 'en'
  const language = hostname.split(".")[0] || "en"; // Default to 'en' if no subdomain or 'www'

  // Fetch the translations for the determined country and language
  const countryTranslations =
    translations[country]?.[language] || translations[country]?.["en"] || {};

  let content = null;
  let localizedStrings = null;

  if (!businessSlug || businessSlug === "null") {
    return {
      redirect: {
        destination: "/404",
        permanent: false,
      },
    };
  }

  try {
    const lang = hostname || "en";
    localizedStrings = await getLocals(lang);
    const apiUrl = `${getBusinessApiDetails}?slug=${businessSlug}`;
    const response = await axios.post(apiUrl, { lan: hostname });
    content = response.data;
  } catch (error) {
    console.log("business slug page Error fetching data:", error.message);
  }

  return {
    props: {
      initialContent: content,
      localizedStrings,
      hostname,
      full,
      translations: countryTranslations,
    },
  };
}

export default function BusinessPage({
  initialContent,
  localizedStrings,
  hostname,
  full,
  countryName,
  countryTranslations,
  translations,
}) {
  let countryTranslationCapital = countryTranslations?.[0]?.toUpperCase();
  let formattedCountryName = countryName.toLowerCase().replace(/ /g, "-");
  const router = useRouter();
  const { businessSlug } = router.query;
  if (businessSlug == "null") {
    return (
      <div>
        <NotFoundPage />
      </div>
    ); // or any loading indicator
  }

  const [content, setContent] = useState(initialContent);
  const [showMap, setShowMap] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const apiUrl = `${getBusinessApiDetails}?slug=${businessSlug}`;
        const response = await axios.post(apiUrl, { lan: hostname });
        setContent(response.data);
      } catch (error) {
        console.log("business slug page 2 Error fetching data:", error);
        if (error.response) {
          // Server responded with a status other than 200
          if (error.response.status === 500) {
            // Handle 500 error specifically
            // router.push("/404"); // Redirect to 404 page
          } else {
            // Handle other errors
            setError("An error occurred while fetching data.");
          }
        } else {
          // No response received (network error, etc.)
          setError("Network error occurred.");
        }
      }
    };

    fetchData();
  }, [businessSlug, hostname, router]);
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 1000) {
        setShowMap(true);
        window.removeEventListener("scroll", handleScroll);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleRouteChange = () => {
      setContent(null); // Clear content when route changes
    };

    router.events.on("routeChangeStart", handleRouteChange);

    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [router]);

  useEffect(() => {
    // Update content when initialContent changes
    setContent(initialContent);
  }, [initialContent]);

  // Check if content is null before accessing its properties
  if (!content) {
    return (
      <div>
        <Skeleton />
      </div>
    ); // or any loading indicator
  }

  const dataToUse = content;
  const obj = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: dataToUse[0].businessName,
    image: dataToUse[0].vectorImage,
    url: dataToUse[0].businessSlug,
    telephone: dataToUse[0].phoneNumber,
    address: {
      "@type": "PostalAddress",
      streetAddress: dataToUse[0].address,
      addressLocality: dataToUse[0].areaName,
      postalCode: dataToUse[0].pinCode,
      addressCountry: "Australia",
    },
  };

  const getData = {
    slug: dataToUse[0]["businessSlug"],
    name: dataToUse[0]["businessName"],
    id: dataToUse[0]["businessId"],
  };
  const fontSize = "text-lg";
  const canonicalUrl = `http://${hostname}/places/${dataToUse[0].businessSlug}`;

  let faqsData;
  if (hostname == "hi") {
    faqsData = [
      {
        ques: `${dataToUse[0].businessName} ${
          localizedStrings && localizedStrings.Index.BusinessPage.FAQHindi.que1
        } ?`,
        ans: `${dataToUse[0].businessName} ${
          localizedStrings && localizedStrings.Index.BusinessPage.FAQHindi.ans1
        } ${dataToUse[0].phoneNumber} ${
          localizedStrings &&
          localizedStrings.Index.BusinessPage.FAQHindi.ansOne
        }`,
      },
      {
        ques: `${dataToUse[0].businessName} ${
          localizedStrings && localizedStrings.Index.BusinessPage.FAQHindi.que2
        } ?`,
        ans: `${dataToUse[0].businessName} ${
          localizedStrings && localizedStrings.Index.BusinessPage.FAQHindi.ans2
        } ${dataToUse[0].rating} ${
          localizedStrings &&
          localizedStrings.Index.BusinessPage.FAQHindi.ansTwo
        }`,
      },
      {
        ques: `${dataToUse[0].businessName} ${
          localizedStrings && localizedStrings.Index.BusinessPage.FAQHindi.que3
        } ?`,
        ans: `${dataToUse[0].businessName} ${
          localizedStrings && localizedStrings.Index.BusinessPage.FAQHindi.ans3
        }
${dataToUse[0].address}
${localizedStrings && localizedStrings.Index.BusinessPage.FAQHindi.ansOne}
`,
      },
      {
        ques: `${dataToUse[0].businessName} ${
          localizedStrings && localizedStrings.Index.BusinessPage.FAQHindi.que4
        }?`,
        ans: `${dataToUse[0].businessName} ${dataToUse[0].areaName} ${
          localizedStrings && localizedStrings.Index.BusinessPage.FAQHindi.ans4
        }`,
      },
      {
        ques: `${dataToUse[0].businessName} ${
          localizedStrings && localizedStrings.Index.BusinessPage.FAQHindi.que5
        }?`,
        ans: `${dataToUse[0].businessName}  ${countryTranslations?.[0]} ${
          localizedStrings && localizedStrings.Index.categoryFirstLine4
        } ${dataToUse[0].categoryNamePlural} ${
          localizedStrings &&
          localizedStrings.Index.BusinessPage.FAQHindi.ansOne
        } `,
      },
      {
        ques: `${
          localizedStrings && localizedStrings.Index.BusinessPage.FAQHindi.que6
        } ${dataToUse[0].businessName} ${
          localizedStrings &&
          localizedStrings.Index.BusinessPage.FAQHindi.queSix
        } ?`,
        ans: ` ${dataToUse[0].areaName} ${
          localizedStrings && localizedStrings.Index.BusinessPage.FAQHindi.ans6
        } ${dataToUse[0].pinCode} ${
          localizedStrings &&
          localizedStrings.Index.BusinessPage.FAQHindi.ansOne
        }`,
      },
    ];
  } else {
    faqsData = [
      {
        ques: `${
          localizedStrings && localizedStrings.Index.BusinessPage.FAQHindi.que1
        } ${dataToUse[0].businessName}?`,
        ans: `${
          localizedStrings && localizedStrings.Index.BusinessPage.FAQHindi.ans1
        } ${dataToUse[0].businessName} ${
          localizedStrings &&
          localizedStrings.Index.BusinessPage.FAQHindi.ansOne
        } ${dataToUse[0].phoneNumber}.`,
      },
      {
        ques: `${
          localizedStrings && localizedStrings.Index.BusinessPage.FAQHindi.que2
        } ${dataToUse[0].businessName}?`,
        ans: `${
          localizedStrings &&
          localizedStrings.Index.BusinessPage.FAQHindi.ans2Change
        } ${dataToUse[0].businessName} ${
          localizedStrings &&
          localizedStrings.Index.BusinessPage.FAQHindi.ansOne
        } ${dataToUse[0].rating} ${
          localizedStrings &&
          localizedStrings.Index.BusinessPage.FAQHindi.ans2Chg
        }.`,
      },
      {
        ques: `${
          localizedStrings && localizedStrings.Index.BusinessPage.FAQHindi.que3
        } ${dataToUse[0].businessName}?`,
        ans: `${
          localizedStrings && localizedStrings.Index.BusinessPage.FAQHindi.ans3
        } ${dataToUse[0].businessName} ${
          localizedStrings &&
          localizedStrings.Index.BusinessPage.FAQHindi.ansOne
        }
${dataToUse[0].address}
`,
      },
      {
        ques: `${
          localizedStrings && localizedStrings.Index.BusinessPage.FAQHindi.que4
        } ${dataToUse[0].businessName}?`,
        ans: `${dataToUse[0].businessName} ${
          localizedStrings && localizedStrings.Index.BusinessPage.FAQHindi.ans4
        } ${dataToUse[0].areaName}.`,
      },
      {
        ques: `${
          localizedStrings && localizedStrings.Index.BusinessPage.FAQHindi.que5
        } ${dataToUse[0].businessName}?`,
        ans: `${dataToUse[0].businessName} ${
          localizedStrings &&
          localizedStrings.Index.BusinessPage.FAQHindi.ansOne
        } ${
          localizedStrings &&
          localizedStrings.Index.BusinessPage.FAQHindi.ansFive
        } ${dataToUse[0].categoryName[0].categoryName} ${
          localizedStrings && localizedStrings.Index.categoryFirstLine4
        } ${countryTranslations?.[0]}`,
      },
      {
        ques: `${
          localizedStrings && localizedStrings.Index.BusinessPage.FAQHindi.que6
        } ${dataToUse[0].businessName} ${
          localizedStrings &&
          localizedStrings.Index.BusinessPage.FAQHindi.queSix
        }?`,
        ans: `${
          localizedStrings && localizedStrings.Index.BusinessPage.FAQHindi.ans6
        } ${dataToUse[0].areaName} is ${dataToUse[0].pinCode}`,
      },
    ];
  }

  const capitalizedString = _.capitalize(dataToUse[0].categoryNamePlural);

  const latitude = dataToUse[0].latitude;
  const longitude = dataToUse[0].longitude;
  const keywords = "test";

  const pageTitle = `${dataToUse[0].businessName}- ${
    dataToUse[0].categoryName[0].categoryName
  } ${
    localizedStrings && localizedStrings.Index.BusinessPage.pageTitle.place
  } ${dataToUse[0].areaName}`;
  const metaDescription = `${pageTitle}`;

  return (
    <Layout
      obj={full}
      hostname={hostname}
      pageTitle={pageTitle}
      metaDescription={metaDescription}
      keyword={keywords}
      canoUrl={canonicalUrl}
      countryName={countryName}
      countryTranslations={translations}
    >
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(obj) }}
        />
      </Head>
      <div className="overflow-auto p-2 md:p-10">
        <div className="md:mx-0">
          <div className="w-full">
            <TopImage
              data={dataToUse}
              hostname={hostname}
              apiData={getData}
              countryName={countryName}
              countryTranslations={translations}
            />
          </div>
          <>
            {/* section2 */}
            {hostname == "hi" ? (
              <div className=" text-justify pt-5 md:pl-0 md:pr-0 md:pt-0 md:pb-4">
                <p className="text-base pb-[60px] md:pt-0 md:pb-0">
                  {dataToUse[0].businessName}{" "}
                  <Link
                    href={`/area/${dataToUse[0].areaSlug}`}
                    className="text-blue-500"
                  >
                    {dataToUse[0].areaName}
                  </Link>{" "}
                  {localizedStrings &&
                    localizedStrings.Index.BusinessPage.Paragraph.line1
                      .con3}{" "}
                  {localizedStrings &&
                    localizedStrings.Index.BusinessPage.Paragraph.line1
                      .con2}{" "}
                  {dataToUse[0].categoryName[0].categoryName}{" "}
                  {localizedStrings &&
                    localizedStrings.Index.BusinessPage.Paragraph.line1
                      .con1}{" "}
                  {localizedStrings &&
                    localizedStrings.Index.BusinessPage.Paragraph.line2
                      .con1}{" "}
                  {countryTranslations?.[0]}{" "}
                  {localizedStrings && localizedStrings.Index.areaExploreBtn1}{" "}
                  {dataToUse[0].count}{" "}
                  <Link
                    href={`/category/${dataToUse[0].categorySlug}-in-${formattedCountryName}`}
                    className="text-blue-500"
                  >
                    {dataToUse[0].categoryNamePlural}
                  </Link>{" "}
                  {localizedStrings &&
                    localizedStrings.Index.BusinessPage.Paragraph.line2
                      .con3}{" "}
                  {dataToUse[0].businessName}{" "}
                  {localizedStrings &&
                    localizedStrings.Index.BusinessPage.Paragraph.line3.con1}
                  {dataToUse[0].address}{" "}
                  {localizedStrings &&
                    localizedStrings.Index.BusinessPage.Paragraph.line3
                      .con2}{" "}
                  {dataToUse[0].website ? (
                    <>
                      {dataToUse[0].businessName}{" "}
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.Paragraph.line4
                          .con1}{" "}
                      {dataToUse[0].website}|
                    </>
                  ) : (
                    ""
                  )}{" "}
                  {dataToUse[0].phoneNumber ? (
                    <>
                      {dataToUse[0].businessName}{" "}
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.Paragraph.line5
                          .con1}{" "}
                      {dataToUse[0].phoneNumber}{" "}
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.Paragraph.line5
                          .con2}
                    </>
                  ) : (
                    ""
                  )}{" "}
                  {dataToUse[0].businessCountIn250Meter ? (
                    <>
                      {dataToUse[0].businessName}{" "}
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.Paragraph.line6
                          .con1}{" "}
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.Paragraph.line6
                          .con2}{" "}
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.Paragraph.line6
                          .con3}{" "}
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.Paragraph.line6
                          .con4}{" "}
                      {dataToUse[0].businessCountIn250Meter}{" "}
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.Paragraph.line6
                          .con5}{" "}
                      {countryTranslations?.[1]}{" "}
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.Paragraph.line6
                          .con6}
                    </>
                  ) : (
                    ""
                  )}{" "}
                  {""}
                  {dataToUse[0].rating && dataToUse[0].numReviews ? (
                    <>
                      {dataToUse[0].businessName}{" "}
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.Paragraph.line7
                          .con1}{" "}
                      {dataToUse[0].numReviews}{" "}
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.Paragraph.line7
                          .con2}{" "}
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.Paragraph.line7
                          .con3}{" "}
                      {dataToUse[0].rating}{" "}
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.Paragraph.line7
                          .con4}{" "}
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.Paragraph.line7
                          .con5}
                    </>
                  ) : (
                    ""
                  )}
                </p>
              </div>
            ) : (
              <div className="text-justify pt-11 md:pl-0 md:pr-0 md:pt-0 md:pb-4">
                <p className="text-base tracking-normal leading-relaxed md:pt-0 md:pb-0">
                  {dataToUse[0].businessName}{" "}
                  {localizedStrings &&
                    localizedStrings.Index.BusinessPage.Paragraph.line1
                      .con1}{" "}
                  {localizedStrings &&
                    localizedStrings.Index.BusinessPage.Paragraph.line1
                      .con2}{" "}
                  {dataToUse[0].categoryName[0].categoryName}{" "}
                  {localizedStrings &&
                    localizedStrings.Index.BusinessPage.Paragraph.line1
                      .con3}{" "}
                  <Link
                    href={`/area/${dataToUse[0].areaSlug}`}
                    className="text-blue-500"
                  >
                    {dataToUse[0].areaName}.
                  </Link>{" "}
                  {localizedStrings &&
                    localizedStrings.Index.BusinessPage.Paragraph.line2
                      .con1}{" "}
                  {localizedStrings &&
                    localizedStrings.Index.BusinessPage.Paragraph.line2
                      .con3}{" "}
                  {dataToUse[0].count}{" "}
                  <Link
                    href={`/category/${dataToUse[0].categorySlug}-in-${formattedCountryName}`}
                    className="text-blue-500"
                  >
                    {dataToUse[0].categoryNamePlural}{" "}
                    {localizedStrings &&
                      localizedStrings.Index.categoryFirstLine4}{" "}
                    {countryTranslationCapital}
                  </Link>
                  .{" "}
                  {localizedStrings &&
                    localizedStrings.Index.BusinessPage.Paragraph.line3
                      .con1}{" "}
                  {dataToUse[0].businessName}{" "}
                  {localizedStrings &&
                    localizedStrings.Index.BusinessPage.Paragraph.line3
                      .con2}{" "}
                  {dataToUse[0].address}.{" "}
                  {dataToUse[0].website ? (
                    <>
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.Paragraph.line4
                          .con1}{" "}
                      {dataToUse[0].businessName}{" "}
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.Paragraph.line4
                          .con2}{" "}
                      {dataToUse[0].website}.
                    </>
                  ) : (
                    ""
                  )}{" "}
                  {dataToUse[0].phoneNumber ? (
                    <>
                      {dataToUse[0].businessName} {""}
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.Paragraph.line5
                          .con2}{" "}
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.Paragraph.line5
                          .con1}{" "}
                      {dataToUse[0].phoneNumber}.{""}
                    </>
                  ) : (
                    ""
                  )}
                  {dataToUse[0].businessCountIn250Meter ? (
                    <>
                      {dataToUse[0].businessName}{" "}
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.Paragraph.line6
                          .con1}{" "}
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.Paragraph.line6
                          .con6}{" "}
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.Paragraph.line6
                          .con4}{" "}
                      {dataToUse[0].businessCountIn250Meter}{" "}
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.Paragraph.line6
                          .con5}{" "}
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.Paragraph.line6
                          .con3}{" "}
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.Paragraph.line6
                          .con2}{" "}
                      {countryTranslations?.[1]}.
                    </>
                  ) : (
                    ""
                  )}
                  {dataToUse[0].rating && dataToUse[0].numReviews ? (
                    <>
                      {dataToUse[0].businessName}{" "}
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.Paragraph.line7
                          .con5}{" "}
                      {dataToUse[0].rating}{" "}
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.Paragraph.line7
                          .con4}{" "}
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.Paragraph.line7
                          .con3}{" "}
                      {dataToUse[0].numReviews}{" "}
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.Paragraph.line7
                          .con2}{" "}
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.Paragraph.line7
                          .con1}
                      .
                    </>
                  ) : (
                    ""
                  )}
                </p>
              </div>
            )}

            {/* section3 */}
            {dataToUse[0].businessesIn250Meter.length > 0 ? (
              <div className="pt-5 md:pl-0 md:pr-0 md:pt-0 md:pb-4">
                {hostname == "hi" ? (
                  <p className="text-base pb-2 font-bold">
                    {dataToUse[0].businessName} {""}
                    {localizedStrings &&
                      localizedStrings.Index.BusinessPage.headingNew}{" "}
                    {""}
                    {localizedStrings &&
                      localizedStrings.Index.BusinessPage.heading}{" "}
                    {""} {""}
                    {localizedStrings &&
                      localizedStrings.Index.BusinessPage.headingWord}
                  </p>
                ) : (
                  <p className="text-base pb-2 font-bold">
                    {localizedStrings &&
                      localizedStrings.Index.BusinessPage.heading}{" "}
                    {""}
                    {dataToUse[0].businessName}
                    {""} {""}
                    {localizedStrings &&
                      localizedStrings.Index.BusinessPage.headingWord}
                  </p>
                )}
                {dataToUse[0].businessesIn250Meter
                  .slice(0, 10)
                  .map((con, index) => (
                    <div
                      key={index}
                      className="flex gap-2 items-center my-2 md:my-0"
                    >
                      <div>
                        {con.categoryIcon == null ? (
                          <Image
                            src={`/assets/img/icons/default-category.svg`}
                            className="h-8 w-8"
                            alt="Icon"
                            height={500}
                            width={500}
                          />
                        ) : (
                          <Image
                            src={`/assets/img/icons/${con.categoryIcon}`}
                            className="h-8 w-8"
                            alt="Icon"
                            height={500}
                            width={500}
                          />
                        )}
                      </div>
                      <div className="flex-col md:flex-row flex md:flex-wrap gap-2">
                        <Link
                          href={`/places/${con.businessSlug}`}
                          className="text-blue-500 font-bold text-base"
                        >
                          {con.businessName} {""}
                        </Link>

                        <p className="text-base">
                          {"("}
                          {con.category}
                          {")"} {con.address}
                        </p>
                      </div>
                    </div>
                  ))}
                <div className="pb-8 md:pb-2 pt-2 text-justify">
                  <p className="text-base tracking-normal leading-relaxed mt-5 md:mt-0">
                    {localizedStrings &&
                      localizedStrings.Index.BusinessPage.line1}{" "}
                    {dataToUse[0].businessName} {""}
                    {localizedStrings &&
                      localizedStrings.Index.BusinessPage.line1New}
                    {dataToUse[0].businessesIn250Meter
                      .slice(10, 30)
                      .map((con, index) => (
                        <span key={index}>
                          {con.businessName}
                          {index <
                          dataToUse[0].businessesIn250Meter.slice(10, 30)
                            .length -
                            1
                            ? ", "
                            : index === 19
                            ? " "
                            : ""}
                        </span>
                      ))}
                    {localizedStrings &&
                      localizedStrings.Index.BusinessPage.line2}
                  </p>
                </div>
                <div className="text-justify pb-7">
                  {dataToUse[0].otherCategoryDetail.length === 1 && (
                    <p>
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.line8}
                      {dataToUse[0].otherCategoryDetail[0].businessName}
                    </p>
                  )}

                  {dataToUse[0].otherCategoryDetail.length === 2 && (
                    <p>
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.line4}
                      {dataToUse[0].otherCategoryDetail
                        .map((item, index) => {
                          return item.businessName;
                        })
                        .join(", ")}
                    </p>
                  )}

                  {dataToUse[0].otherCategoryDetail.length > 2 && (
                    <p className="text-base tracking-normal leading-relaxed">
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.line3}{" "}
                      {""}
                      {dataToUse[0].businessName}
                      {""} {""}
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.line3New}{" "}
                      {dataToUse[0].otherCategoryDetail
                        .map((item) => item.businessName)
                        .join(", ")}
                    </p>
                  )}
                </div>
              </div>
            ) : null}
            {/* section4 */}
            <div className="pb-10">
              <div className="flex items-center gap-2 pb-2">
                <div>
                  <Image
                    src="/assets/img/distance.svg"
                    className="md:h-8 md:w-8 h-10 w-10"
                    height={500}
                    width={500}
                    alt="image"
                  />
                </div>
                <div>
                  <p className="md:text-xl text-base">
                    <b>
                      {localizedStrings &&
                        localizedStrings.Index.BusinessPage.headingLine}
                    </b>
                  </p>
                </div>
              </div>
              <div className="flex">
                <div className="space-y-5 md:space-y-0">
                  {dataToUse[0].firstFive.map((con, index) => (
                    <div className="flex items-center gap-2 pb-2" key={index}>
                      <div>
                        <FaCircle className="text-blue-400 md:w-2 md:h-2 h-4 w-4" />
                      </div>

                      {hostname == "hi" ? (
                        <div>
                          <p className="text-base tracking-normal leading-relaxed mx-2">
                            {dataToUse[0].businessName}{" "}
                            {localizedStrings &&
                              localizedStrings.Index.BusinessPage.line6}
                            <Link
                              href={`/places/${con.businessSlug}`}
                              className="text-blue-500"
                            >
                              {" "}
                              {con.businessName}
                            </Link>{" "}
                            {""}
                            {localizedStrings &&
                              localizedStrings.Index.BusinessPage.line5}{" "}
                            {localizedStrings &&
                              localizedStrings.Index.BusinessPage.line7}
                          </p>
                        </div>
                      ) : (
                        <div>
                          <p className="text-base tracking-normal leading-relaxed mx-2">
                            {localizedStrings &&
                              localizedStrings.Index.BusinessPage.line5}{" "}
                            {dataToUse[0].businessName}{" "}
                            {localizedStrings &&
                              localizedStrings.Index.BusinessPage.line6}
                            <Link
                              href={`/places/${con.businessSlug}`}
                              className="text-blue-500"
                            >
                              {" "}
                              {con.businessName}
                            </Link>{" "}
                            {localizedStrings &&
                              localizedStrings.Index.BusinessPage.line7}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* section5 */}
            <div className="card bg-white md:shadow-lg">
              <div className="pb-5">
                <div className="flex flex-col md:flex-row gap-[17px] md:gap-[200px] items-baseline ">
                  <div className="mb-5 md:mb-0">
                    <div className="flex gap-2 pb-4 items-center">
                      <div>
                        <Image
                          src="/assets/img/rating.svg"
                          className="md:w-6 md:h-6 w-8"
                          height={500}
                          width={500}
                          alt="image"
                        />
                      </div>
                      <div>
                        <p className="text-xl md:text-base">
                          <b>
                            {localizedStrings &&
                              localizedStrings.Index.BusinessPage.Rating}
                          </b>
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 items-center">
                      <p className="text-base">{dataToUse[0].rating}/5</p>
                    </div>
                  </div>
                  <div className="mb-5 md:mb-0">
                    <div className="flex gap-2 pb-4 items-center">
                      <div>
                        <Image
                          src="/assets/img/contact-person.svg"
                          className="md:w-6 md:h-6 w-8"
                          width={500}
                          height={500}
                          alt=""
                        />
                      </div>
                      <div>
                        <p className="text-xl md:text-base">
                          <b>
                            {localizedStrings &&
                              localizedStrings.Index.BusinessPage.Contact}
                          </b>
                        </p>
                      </div>
                    </div>
                    {dataToUse[0].phoneNumber ? (
                      <div className="flex gap-2 items-center">
                        <div className="text-blue-400">
                          <IoCall className="text-xl" />
                        </div>
                        <div className="text-base">
                          {dataToUse[0].phoneNumber}
                        </div>
                      </div>
                    ) : (
                      ""
                    )}
                    {dataToUse[0].website ? (
                      <div className="flex gap-2 items-center">
                        <div className="text-blue-400">
                          <FaGlobeAsia className="text-xl" />
                        </div>
                        <div className="break-all text-base">
                          {dataToUse[0].website}
                        </div>
                      </div>
                    ) : (
                      ""
                    )}
                  </div>
                  <div>
                    <div className="flex gap-2 pb-4 items-center">
                      <div>
                        <Image
                          src="/assets/img/sign-post.svg"
                          className="md:w-6 md:h-6 w-8"
                          height={500}
                          width={500}
                          alt=""
                        />
                      </div>
                      <div>
                        <p className="text-xl md:text-base">
                          <b>
                            {localizedStrings &&
                              localizedStrings.Index.BusinessPage.Address}
                          </b>
                        </p>
                      </div>
                    </div>
                    <div>
                      <p className="text-base">{dataToUse[0].address}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* section6 */}
            {showMap ? (
              <Suspense fallback={<div>Loading map...</div>}>
                <LazyMap
                  srcLink={`https://maps.google.com/maps?q=${latitude},${longitude}&output=embed`}
                />
              </Suspense>
            ) : (
              <div>Scroll down or click here to view the map.</div> // Placeholder content
            )}
            {/* section7 */}
            <FAQ data={faqsData} />

            {/* additional point */}
            <BusinessReview
              businessSlug={businessSlug}
              businessName={dataToUse[0].businessName}
            />
            {/* additional point */}

            {/* section8 */}
            <Reviews businessSlug={businessSlug} fontSize={fontSize} />
            {/* section9 */}
            <ReviewForm apiData={getData} />
            {/* section10 */}

            {/* <div className="text-xl">
              <p className="text-lg">
                <b>
                  {localizedStrings &&
                    localizedStrings.Index.BusinessPage.Search}
                </b>
              </p>
              {hostname == "hi" ? (
                <Link
                  href={`/category/${dataToUse[0].categorySlug}-in-${dataToUse[0].areaSlug}`}
                  type="button"
                  className="text-white bg-blue-500 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 dark:bg-blue-600 dark:hover:bg-blue-700 focus:outline-none dark:focus:ring-blue-800"
                >
                  {dataToUse[0].areaName}{" "}
                  {localizedStrings && localizedStrings.Index.Area.IN}{" "}
                  {capitalizedString}
                </Link>
              ) : (
                <Link
                  href={`/category/${dataToUse[0].categorySlug}-in-${dataToUse[0].areaSlug}`}
                  type="button"
                  className="text-white bg-blue-500 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 dark:bg-blue-600 dark:hover:bg-blue-700 focus:outline-none dark:focus:ring-blue-800"
                >
                  {capitalizedString}{" "}
                  {localizedStrings && localizedStrings.Index.Area.IN}{" "}
                  {dataToUse[0].areaName}
                </Link>
              )}
            </div> */}
          </>
        </div>
      </div>
    </Layout>
  );
}
